import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TextInput } from '../../../../../input/TextInput'
import { SelectOptionInput } from '../../../../../input/selectOptionInput/SelectOptionInput'
import { NumberInput } from '../../../../../input/NumberInput'
import FormSection from '../../../../../formSection/FormSection'
import { InputTime } from '../../../../../input/inputTime/InputTime'

import { ClubModel } from '../../../../../../types/court/court'
import { timeZoneOptions } from '../../../../../../utils/timeZones'

type ClubField = keyof Pick<
  ClubModel,
  | 'name'
  | 'openTime'
  | 'closeTime'
  | 'freeCancellation'
  | 'startIncrementMinutes'
  | 'maxReschedules'
  | 'passExtensionDays'
  | 'timeZone'
>

const NUMERIC_FIELDS: ClubField[] = [
  'freeCancellation',
  'startIncrementMinutes',
  'maxReschedules',
  'passExtensionDays',
]

/** Durations offered to customers (minutes); the club picks any subset. */
const DURATION_CHOICES = [30, 45, 60, 75, 90, 105, 120, 150, 180]
const INCREMENT_CHOICES = [15, 30, 60]

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

type ClubFormSectionProps = {
  details: ClubModel
  isEditMode: boolean
  onChange: (details: ClubModel) => void
  onValidityChange: (isValid: boolean) => void
}

export const ClubFormSection = ({
  details,
  isEditMode,
  onChange,
  onValidityChange,
}: ClubFormSectionProps) => {
  const { t } = useTranslation('clubs')
  const [fieldsValidity, setFieldsValidity] = useState<
    Record<ClubField, boolean>
  >({
    name: true,
    openTime: true,
    closeTime: true,
    freeCancellation: true,
    startIncrementMinutes: true,
    maxReschedules: true,
    passExtensionDays: true,
    timeZone: true,
  })

  // Built once per form; the club's saved zone is kept even if the browser knows it under another name.
  const timeZones = useMemo(() => timeZoneOptions(details.timeZone), [details.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleIsValid =
    Boolean(details.openTime) &&
    Boolean(details.closeTime) &&
    details.openTime < details.closeTime

  const openMinutes = scheduleIsValid ? toMinutes(details.closeTime) - toMinutes(details.openTime) : 0
  const durationsAreValid =
    details.sessionDurations.length > 0 && details.sessionDurations.every(duration => duration <= openMinutes)

  const formIsValid =
    Boolean(details.name.trim()) &&
    scheduleIsValid &&
    durationsAreValid &&
    Number.isInteger(details.maxReschedules) &&
    details.maxReschedules >= 0 &&
    Number.isInteger(details.passExtensionDays) &&
    details.passExtensionDays > 0 &&
    Number.isFinite(details.freeCancellation) &&
    details.freeCancellation >= 0 &&
    Object.values(fieldsValidity).every(Boolean)

  useEffect(() => {
    onValidityChange(formIsValid)
  }, [formIsValid, onValidityChange])

  const handleChange = (name: string, value: string, isValid: boolean) => {
    const field = name as ClubField

    const parsedValue = NUMERIC_FIELDS.includes(field) ? Number(value) : value

    onChange({
      ...details,
      [field]: parsedValue,
    })

    setFieldsValidity(previous => ({
      ...previous,
      [field]: isValid,
    }))
  }

  const toggleDuration = (duration: number) =>
    onChange({
      ...details,
      sessionDurations: details.sessionDurations.includes(duration)
        ? details.sessionDurations.filter(item => item !== duration)
        : [...details.sessionDurations, duration].sort((a, b) => a - b),
    })

  return (
    <FormSection
      title={t(
        isEditMode ? 'clubs.edit.details.title' : 'clubs.new.details.title'
      )}
      subtitle={t(
        isEditMode
          ? 'clubs.edit.details.subtitle'
          : 'clubs.new.details.subtitle'
      )}
    >
      <div className="row g-3">
        <div className="col-12 col-md-8">
          <TextInput
            name="name"
            label={t('clubs.form.fields.name')}
            value={details.name}
            onChange={handleChange}
            errorMessage={t('clubs.form.validation.required')}
            required
          />
        </div>
        <div className="col-12 col-md-4">
          <NumberInput
            name="freeCancellation"
            label={t('clubs.form.fields.free-cancellation')}
            value={String(details.freeCancellation)}
            min={1}
            step={1}
            onChange={handleChange}
            errorMessage={t('clubs.form.validation.minimum-cancellation')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <InputTime
            name="openTime"
            label={t('clubs.form.fields.open-time')}
            value={details.openTime}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <InputTime
            name="closeTime"
            label={t('clubs.form.fields.close-time')}
            value={details.closeTime}
            onChange={handleChange}
            errorMessage={
              scheduleIsValid ? undefined : t('clubs.form.validation.schedule')
            }
            required
          />
        </div>
        <div className="col-12">
          <div className="field" role="group" aria-label={t('clubs.form.fields.session-durations')}>
            <span className="eyebrow">{t('clubs.form.fields.session-durations')}</span>
            <div className="tag-row">
              {DURATION_CHOICES.map(duration => {
                const selected = details.sessionDurations.includes(duration)
                return (
                  <button
                    key={duration}
                    type="button"
                    className={`badge ${selected ? 'badge-accent' : ''}`}
                    aria-pressed={selected}
                    onClick={() => toggleDuration(duration)}
                  >
                    {duration} min
                  </button>
                )
              })}
            </div>
            {!durationsAreValid && (
              <div className="input-error">{t('clubs.form.validation.session-durations')}</div>
            )}
          </div>
        </div>
        <div className="col-12 col-md-6">
          <SelectOptionInput
            name="startIncrementMinutes"
            label={t('clubs.form.fields.start-increment')}
            value={String(details.startIncrementMinutes)}
            list={INCREMENT_CHOICES.map(minutes => ({ value: String(minutes), label: `${minutes} min` }))}
            infoMessage={t('clubs.form.fields.start-increment-info')}
            onChange={(name, value) => handleChange(name, value, true)}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <SelectOptionInput
            name="timeZone"
            label={t('clubs.form.fields.time-zone')}
            value={details.timeZone ?? ''}
            list={timeZones}
            errorMessage={t('clubs.form.validation.required')}
            onChange={(name, value) => handleChange(name, value, Boolean(value))}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="maxReschedules"
            label={t('clubs.form.fields.max-reschedules')}
            value={String(details.maxReschedules)}
            min={0}
            step={1}
            onChange={handleChange}
            onValidate={value => Number.isInteger(Number(value)) && Number(value) >= 0}
            errorMessage={t('clubs.form.validation.non-negative-integer')}
            infoMessage={t('clubs.form.fields.max-reschedules-info')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="passExtensionDays"
            label={t('clubs.form.fields.pass-extension')}
            value={String(details.passExtensionDays)}
            min={1}
            step={1}
            onChange={handleChange}
            onValidate={value => Number.isInteger(Number(value)) && Number(value) > 0}
            errorMessage={t('clubs.form.validation.positive-integer')}
            infoMessage={t('clubs.form.fields.pass-extension-info')}
            required
          />
        </div>
      </div>
    </FormSection>
  )
}
